<template>
  <div class="boon-row">
    <div class="cell arrow narrow" @click="toggleExpanded">
      <span v-show="calcExpanded">↘</span>
      <span v-show="!calcExpanded">→</span>
    </div>

    <div class="cell">
      <router-link :to="link">
        <b-icon v-if="!calcCompany.companyName" icon="pencil-fill" />
        <span>{{ calcCompany.companyName }}</span>
        <b-icon v-if="isMissingUeFile" icon="file-earmark-x" variant="danger" :scale="1.5" class="ml-1" />
        <b-iconstack v-if="isDoNotUse" :font-scale="1.5" class="ml-2">
          <b-icon stacked icon="circle" variant="danger" />
          <b-icon stacked icon="hand-index" :scale="0.65" :shift-h="-0.5" variant="danger" />
          <b-icon stacked icon="dash" rotate="45" :shift-h="2.75" :shift-v="-2.75" variant="danger" />
          <b-icon stacked icon="dash" rotate="45" :shift-h="-2.75" :shift-v="2.75" variant="danger" />
        </b-iconstack>
      </router-link>
    </div>

    <div class="cell cell-short-text">
      <router-link :to="link">{{ calcCompany.phone }}</router-link>
    </div>

    <div class="cell">
      <router-link :to="link">{{ calcCompany.email }}</router-link>
    </div>

    <div class="cell cell-address">
      <router-link :to="link">
        {{ calcCompany.address.street }} {{ calcCompany.address.zipCode }} {{ calcCompany.address.city }}
      </router-link>
    </div>

    <div class="cell cell-short-text">
      <router-link :to="link">{{ calcCompany.mainBusiness.join() }}</router-link>
    </div>

    <div v-if="calcExpanded" class="boon-row details">
      <div v-for="person in calcPersons" :key="person._id" class="group">
        <person-list-row
          :person="person"
          :selected="selectedPersons.includes(person._id)"
          :selector-visible="!!selectedPersons.length"
          @update:selected="onPersonSelectedToggle"
        />
      </div>

      <button class="add-person-button" @click="onAddPersonClick">
        <b-icon icon="plus" scale="1.2" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import constants from '@/constants';

import PersonListRow from './PersonListRow';

import { byCompanyId as getCompanyFilesByCompanyId } from '@/store/companyFiles/getterTypes';
import { mapCompanyFilesGetters } from '@/store/companyFiles/namespace';

const unassignedCompany = {
  _id: '',
  companyName: '[Unassigned]',
  doNotUse: true,
  address: {},
  mainBusiness: [],
};

export default {
  name: 'CompanyListRow',

  components: {
    PersonListRow,
  },

  props: {
    index: {
      type: Number,
      required: true,
    },
    source: {
      type: Object,
      required: true,
    },
    selection: {
      type: Array,
      required: true,
    },
    onToggleExpanded: {
      type: Function,
      default: undefined,
    },
    onSelectionUpdate: {
      type: Function,
      default: undefined,
    },
  },

  computed: {
    ...mapCompanyFilesGetters([getCompanyFilesByCompanyId]),

    calcCompany() {
      return (this.source && this.source.company) || unassignedCompany;
    },

    calcPersons() {
      return this.source.persons;
    },

    calcExpanded() {
      return this.source.expanded == null ? this.source.hasFilter : this.source.expanded;
    },

    filesDoc() {
      return this.calcCompany && this.calcCompany._id ? this[getCompanyFilesByCompanyId](this.calcCompany._id) : null;
    },

    isDoNotUse() {
      return this.calcCompany.doNotUse || this.calcCompany.role === 'Not OK';
    },

    isMissingUeFile() {
      if (!this.calcCompany || !this.calcCompany._id) {
        return false;
      }

      const projectFiles = (this.filesDoc && this.filesDoc.projectFiles) || {};
      if (
        this.calcCompany.registrationLinks &&
        this.calcCompany.registrationLinks.some(({ projectId }) => !projectFiles[projectId])
      ) {
        return true;
      }

      const missingStatus = constants.companyFiles.statuses.NOT_LOADED;

      return Object.values(projectFiles).some(({ files }) => {
        if (!files) {
          return false;
        }

        const ueFile = files.find(({ fileName }) => fileName === 'ueFile');
        return !ueFile || ueFile.status === missingStatus;
      });
    },

    link() {
      return this.calcCompany._id ? { name: 'EditCompany', params: { _id: this.calcCompany._id } } : {};
    },

    selectedPersons() {
      if (!this.calcCompany) {
        return [];
      }
      const companySelection = this.selection.find(({ companyId }) => companyId === this.calcCompany._id);

      return (companySelection && companySelection.items) || [];
    },
  },

  methods: {
    ...mapActions(['addPerson']),

    toggleExpanded() {
      if (this.onToggleExpanded) {
        this.onToggleExpanded({ id: this.calcCompany._id, value: !this.calcExpanded });
      }
    },

    async onAddPersonClick() {
      const person = await this.addPerson({ newPersonProperties: { companyId: this.calcCompany._id } });
      this.$router.push({ name: 'EditPerson', params: { _id: person._id } });
    },

    onPersonSelectedToggle(person, selected) {
      const items = selected
        ? [...this.selectedPersons, person._id]
        : this.selectedPersons.filter((_id) => _id !== person._id);
      this.onSelectionUpdate(this.calcCompany, items);
    },
  },
};
</script>

<style scoped>
a:visited {
  color: inherit;
}

.boon-row {
  display: flex;
  flex-flow: row wrap;
}

.cell a {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.cell {
  padding: 0.875rem !important;
}

.cell:first-child {
  padding-left: 1.5rem;
}

.cell:last-child {
  padding-right: 1.5rem;
}

.cell:not(.narrow) {
  width: 300px;
  padding-right: 10px !important;
}

.arrow {
  cursor: pointer;
  min-width: 46px;
}

.details {
  display: flex;
  flex-flow: row wrap;
  flex-grow: 1;
  width: 100%;
  padding: 10px 0;
  padding-left: 1.5em;
}

.details .group {
  width: 100%;
}

.details .group > .boon-row {
  background: #fff;
  padding: 5px 0;
}

.details .group:nth-of-type(odd) > .boon-row {
  background: #efefef;
}

.add-person-button {
  color: black;
}

.company-do-not-use-icon {
  width: 16px;
  margin-left: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/CompanyListRow.vue